import { Injectable } from '@angular/core';
import { catchError, map, mergeMap } from 'rxjs/operators';
import { forkJoin, of } from 'rxjs';
import { Actions, Effect, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { QueryParamsModel } from '../../_base/crud';
import { AppState } from '../../../core/reducers';
import {
  ClearInsuranceErrors,
  InsuranceActionTypes,
  InsuranceCreated,
  InsuranceDeleted,
  InsuranceOnServerCreated,
  InsuranceOnServerDeleted,
  InsuranceOnServerUpdated,
  InsurancesActionToggleLoading,
  InsurancesPageCancelled,
  InsurancesPageLoaded,
  InsurancesPageRequested,
  InsurancesPageToggleLoading,
  InsuranceUpdated,
  ThrowInsuranceError,
} from '../_actions/insurance.actions';
import { Router } from '@angular/router';
import { InsurancesService } from '../_services/insurance.service';

@Injectable()
export class InsuranceEffects {
  showPageLoadingDistpatcher = new InsurancesPageToggleLoading({ isLoading: true });
  hidePageLoadingDistpatcher = new InsurancesPageToggleLoading({ isLoading: false });
  showActionLoadingDistpatcher = new InsurancesActionToggleLoading({ isLoading: true });
  hideActionLoadingDistpatcher = new InsurancesActionToggleLoading({ isLoading: false });

  @Effect()
  loadInsurancesPage$ = this.actions$.pipe(
    ofType<InsurancesPageRequested>(InsuranceActionTypes.InsurancesPageRequested),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showPageLoadingDistpatcher);
      const requestToServer = this.insurance.findInsurances(payload.page);
      const lastQuery = of(payload.page);
      return forkJoin(requestToServer, lastQuery).pipe(
        catchError((error) => {
          return of([error]);
        }),
        map((response) => {
          if (response[0].error) {
            if (response[0].status === 401) {
              return response[0];
            } else if (response[0].status === 403) {
              this.router.navigateByUrl('/error/403');
              return new InsurancesPageCancelled();
            } else {
              this.store.dispatch(new ThrowInsuranceError({ error: response[0].error }));
              return new InsurancesPageCancelled();
            }
          } else {
            const result = response[0];
            // tslint:disable-next-line:no-shadowed-variable
            const lastQuery: QueryParamsModel = response[1];
            this.store.dispatch(new ClearInsuranceErrors());
            return new InsurancesPageLoaded({
              insurances: result.data,
              totalCount: result.count,
              page: lastQuery,
            });
          }
        }),
      );
    }),
  );

  @Effect()
  deleteInsurance$ = this.actions$.pipe(
    ofType<InsuranceOnServerDeleted>(InsuranceActionTypes.InsuranceOnServerDeleted),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.insurance.deleteInsurance(payload.id).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map(() => {
          this.store.dispatch(this.hideActionLoadingDistpatcher);
          return new InsuranceDeleted(payload);
        }),
      );
    }),
  );

  @Effect()
  updateInsurance$ = this.actions$.pipe(
    ofType<InsuranceOnServerUpdated>(InsuranceActionTypes.InsuranceOnServerUpdated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.insurance.updateInsurance(payload.insurance).pipe(
        catchError((error) => {
          return of({ error });
        }),
        map((res) => {
          if (res.error) {
            return new ThrowInsuranceError({ error: res.error });
          } else {
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            this.store.dispatch(new ClearInsuranceErrors());
            return new InsuranceUpdated(payload);
          }
        }),
      );
    }),
  );

  @Effect()
  createInsurance$ = this.actions$.pipe(
    ofType<InsuranceOnServerCreated>(InsuranceActionTypes.InsuranceOnServerCreated),
    mergeMap(({ payload }) => {
      this.store.dispatch(this.showActionLoadingDistpatcher);
      return this.insurance.createInsurance(payload.insurance).pipe(
        catchError((error) => {
          return of(error);
        }),
        map((res) => {
          if (res.status === 201) {
            this.store.dispatch(new ClearInsuranceErrors());
            this.store.dispatch(this.hideActionLoadingDistpatcher);
            return new InsuranceCreated({ insurance: res });
          } else {
            return new ThrowInsuranceError({ error: res });
          }
        }),
      );
    }),
  );

  constructor(
    private actions$: Actions,
    private insurance: InsurancesService,
    private store: Store<AppState>,
    private router: Router,
  ) {}
}
